import { renderBylineParts, type Provenance } from '@/lib/provenance';

/**
 * CS1 — the honest byline block. Renders ONE of the two truthful grammars from a piece's
 * derived provenance (see src/lib/provenance.ts).
 *
 * Grammar A names the producer lane plus the reviewer's real masthead name, with their ecosystem
 * handle rendered as a LINK to a real, reputation-bearing account — pseudonymous-but-verifiable.
 * Grammar B is HONEST BY OMISSION: it names only the desk that drafted the piece and says nothing
 * about review. It carries no policy link and no apology; absence of a review clause IS the signal.
 * NEVER renders the author field / a uid.
 *
 * The grammar STRINGS live only in the provenance module (renderBylineParts) — this component is
 * a dumb renderer, which is what keeps the grep-proof invariant true.
 *
 * CS1b — when `receiptHref` is supplied (published, public pages only), a quiet
 * "How this was made" link opens the provenance page, which in turn links the raw
 * machine-readable JSON. The caller gates the href on public-published state; both the page and
 * the /api/receipts route are themselves fail-closed.
 *
 * CS2 — for a desk BRIEF, the caller passes `via` (the source outlet name, from
 * `provenance.viaOutlet`). It renders a quiet "via {Outlet}" attribution beside the byline
 * (attribute-don't-adopt: the outlet reported it, the desk summarized). Absent/blank `via` →
 * nothing (never "via undefined"); the caller only supplies it for brief-lane docs.
 */
export default function DeskByline({
  provenance,
  receiptHref,
  via,
  className = '',
}: {
  provenance: Provenance;
  receiptHref?: string;
  via?: string;
  className?: string;
}) {
  const parts = renderBylineParts(provenance);

  return (
    <div
      className={`mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-parchment-dim ${className}`}
    >
      {parts.grammar === 'A' ? (
        <span className="text-parchment">
          {parts.prefix}
          <span>{parts.name}</span>
          {parts.handle && (
            <>
              {' ('}
              {parts.handleUrl ? (
                <a
                  href={parts.handleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-parchment underline-offset-2 hover:text-scout-gold hover:underline"
                >
                  {parts.handle}
                </a>
              ) : (
                <span>{parts.handle}</span>
              )}
              {')'}
            </>
          )}
        </span>
      ) : (
        <span className="text-parchment">{parts.text}</span>
      )}
      {via && <span className="text-parchment-dim">via {via}</span>}
      {receiptHref && (
        <>
          <span aria-hidden className="text-parchment-dim/50">
            ·
          </span>
          <a
            href={receiptHref}
            className="text-parchment-dim underline-offset-2 hover:text-scout-gold hover:underline"
            title="See how this piece was produced and published"
          >
            How this was made
          </a>
        </>
      )}
    </div>
  );
}
